import { IPatient, ISitecoreProduct } from '../models/prosites';
import { ICartItemInfo } from './pro-sites';

export enum sides {
    right = 'right',
    left = 'left',
    binaural = 'binaural',
}

export enum sidesUpperCase {
    right = 'R',
    left = 'L',
    binaural = 'B',
}

export enum receiverFitTypes {
    standard = 'Standard',
    earmold = 'Earmold',
    ap = 'Absolute Power',
    chr = 'Custom Healthable',
}

export enum OptionTypes {
    FaceplateColor = 3,
    ShellColor = 4,
}

export const optionKinds: string[] = [
    'hidden',
    'select',
    'dual-select',
    'multi',
    'dual-multi',
    'single',
    'dual-single',
    'select-nd',
    'dual-select-nd',
    'dual-single-nd',
    'single, inline',
    'display',
];

export interface IProductDetail extends ISitecoreProduct {
    catalogId: number;
    earmoldCategory: string;
    compatibility: string;
    designId: number;
    fitting: string[][];
    imageBase: string;
    isCustom: boolean;
    isSnapfit: boolean;
    isSnapFit2: boolean;
    options: IOption[];
    ricEarmoldOptions: IOption[];
    ricAPOptions: IOption[];
    bteEarmoldOptions: IOption[];
    standardColors: IProductImage[];
    pageGuid: string;
    inventoryStatuses: IInventoryStatus[];
    accessories: IRelatedItem[];
    commonSupplies: IRelatedItem[];
    defaultWarranty: number;
    additionalWarrantyOptions: number[];
    technologies: string[];
    faceplateColor: string | undefined;
    shellColor: string | undefined;
    earmolds: IEarmoldSelection[];
    earmoldOptions: IOption[];
    productRules: IActions[];
    earmoldRules: IActions[];
    isCros: boolean;
    existingPairAidDescription: string;
    crosHearingAidTechnologies: IPairAidPart[];
    baseProductId: string;
    existing: ICartItemInfo;
}

export interface IInventoryStatus {
    partNumber: string;
    countryCode: string;
    status: string;
    statusColor: string;
    statusText: string;
}

export interface IRelatedItem {
    itemGuid: string;
    partNumber: string;
    description: string;
    imagePath: string;
    quantity: number;
}

export interface IProductImage {
    color: string;
    imageUrl: string;
    itemGuid: string;
    partNumber: string;
    swatchUrl: string;
}

export interface IProductHoverImage {
    imageUrl: string;
    color: string;
    shellColor: string;
    partNumber?: string;
}

export interface ICustomColor {
    id: number;
    color: string;
    colorName: string;
    isdefault: boolean;
    swatchUrl: string;
}

export interface IEarmoldSelection {
    id: number;
    parentItem: number;
    name: string;
    key: string;
    selected: boolean;
    designId: number;
    noteHtml: string;
    items: IEarmoldSelection[];
}

export interface IPatientDevice {
    device: IDeviceHistory;
    pairDevice: IDeviceHistory;
    patientFirstName: string;
    patientLastName: string;
    unavailable: boolean;
}

export interface IDeviceHistory {
    style: string;
    options: string[];
    matrix: string;
    serialNumber: string;
}

//written as per Starkey.Pro.EStore.Interfaces.IProductOrder
//TODO: Change IOrderSelection to IProductOrder
export interface IOrderDetails {
    itemGuid: string;
    baseProductId: string;
    partNumber: string;
    orderType: string;
    cartItemTypeId?: number;
    earmoldCategory?: string;
    catalogId: number;
    isCustom: boolean;
    quantity: number;
    ampQuantityLeft: number;
    side: string | null;
    existingItemId: string;
    pageGuid: string;
    selectedImage: string;
    selectedColor: string;
    selectedItemNumber: string;
    designId: number;
    productUrl: string;
    description: string;
    brand: string;
    rightSelectedIds: number[];
    leftSelectedIds: number[];
    earmoldDesignId?: number;
    rightEarmoldIds: number[];
    leftEarmoldIds: number[];
    receivers: IReceiverPart[] | null;
    thinTubes: IThinTubePart[];
    accessories: IAccessoryOrder[];
    patient: IPatient | null;
    warranty: number;
    specialInstructions: string | null;
    showSpecialInstructions: boolean;
    cros: boolean;
    pairAid: IPairAidPart | null;
    useManufacturerSelect: boolean;
    PatientIdentificationNumber: any;
    technology: string;
    receiverFit: string | null;
    impressions: string | null;
    runRules: number;
    productOptions?: IOption[];
    earmoldOptions?: IOption[];
    showEarmoldForMonauralCros?: boolean;
    additionalReceivers?: boolean;
}

export interface ISelectedOptionIds {
    leftSelectedIds: number[];
    rightSelectedIds: number[];
}

export interface RulesResult {
    selectedOptionIds: ISelectedOptionIds;
    options: IOption[];
}

export interface StateResult {
    productDetails?: IProductDetail;
    orderDetails?: IOrderDetails;
    isEarmold?: boolean;
}

export interface IOption {
    id: number;
    name: string;
    english: string;
    note: string;
    groupType?: number;
    isDefault?: boolean;
    hidden?: boolean;
    patientOnly?: boolean;
    partNumber: string;
    pcIds: number[];
    onSelect: IActions;
    onUnselect: IActions;
    incompatible: IActions;
    items: IOption[];
    leftPartNumber: string;
    kind: string;
    groups: IOption[] | undefined;
    options: IOption[] | undefined;
    parent?: IOption | null;
    isAvailableRight: boolean;
    isAvailableLeft: boolean;
}

export interface IActions {
    selects: number[];
    unselects: number[];
    activates: number[];
    deactivates: number[];
    hides: number[];
    hidesFrom: number[];
    showIf: number[];
    prefers: number[];
    reevaluates: number[];
    requires: number[];
    selected: number;
    unselected: number;
    incompatible: number;
    unDeactivates: number[];
    ignorePrefers: boolean;
}

//written as per Starkey.Pro.Devices.ReceiverPart
export interface IReceiverPart {
    side: string | undefined;
    cableLength: number | undefined;
    gain: string | undefined;
    quantity: number | undefined;
    partType: number;
    right: string;
    left: string;
    useAngledParts: boolean;
    type: string;
    partNumber: string;
    isAdditionalReceiver: boolean;
    gainAsLetter: string;
}

//written as per Starkey.Pro.Devices.ThinTubePart
export interface IThinTubePart {
    side: string;
    lengthOptionId: number;
    quantity: number;
}

//written as per Starkey.Pro.EStore.AccessoryOrder
export interface IAccessoryOrder {
    id: number;
    quantity: number;
    partNumber: string;
    isCharger?: boolean;
    productType?: string;
}

//written as per Starkey.Pro.Devices.PairAidPart
export interface IPairAidPart extends IPart {
    displayName: string;
}

//written as per Starkey.Pro.Devices.Part
export interface IPart {
    catalog: number;
    designId: number;
    description: string;
    categories: IOptionCategory[];
    isStandard: boolean;
}

//written as per Starkey.OnlineOrdering.OOF.OptionGroup
// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IOptionCategory {}

interface IExtendedWarranty {
    name: string;
    years: number;
    catalogs: number[];
}

export interface IThreshold {
    response: string;
    noResponse: string;
    frequency: number;
    label: string;
    required: boolean;
    side: string;
}

export interface IAudiogram {
    right: {
        air: { curve: string | ',,,,,,,,,' };
        bone: { curve: string | ',,,,,,,,,' };
        MCL?: number | string | null;
        UCL?: number | string | null;
        lossType?: number | null;
    };
    left: {
        air: { curve: string | ',,,,,,,,,' };
        bone: { curve: string | ',,,,,,,,,' };
        MCL?: number | string | null;
        UCL?: number | string | null;
        lossType?: number | null;
    };
    MCL?: number | string | null;
    UCL?: number | string | null;
}

export interface IAudiogramResponse {
    matrix: IMatrix | null;
    vent: string | null;
    loss: ILoss | null;
}

export interface IMatrix {
    output: number;
    gain: number;
}

export interface ILoss {
    id: number;
    name: string;
    english: string;
}

export enum LossType {
    Mild = 1, // Gain less than 43
    Moderate = 2, // Gain 43-59
    Severe = 3, // Gain 60-75
    Profound = 4, // Gain > 75
}

export interface IDeviceScanOnFile {
    valid: boolean | undefined;
    isStandard: boolean | undefined;
    patientFirstName: string | undefined;
    patientLastName: string | undefined;
    otherSide: string | undefined;
    side: string | undefined;
    serialNumber: string | undefined;
    isBadQuality: boolean | undefined;
}

export interface ChangeResult {
    changesMade?: boolean;
    error?: string;
}

// helper functions
export const createStateResult = () => {
    const stateResult: StateResult = {
        orderDetails: {} as any,
        productDetails: {} as any,
    };

    return stateResult;
};
